import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Building2,
  Store,
  Sparkles,
  Crown,
  QrCode,
  Megaphone,
  Headphones,
  Check,
  ShieldCheck,
  Activity,
  ListMusic,
  Radio,
} from "lucide-react";

import { MwHeader } from "@/components/mediaweb/MwHeader";
import { MwFooter } from "@/components/mediaweb/MwFooter";

const targets = [
  {
    icon: Building2,
    title: "Centres commerciaux",
    text: "Une identité sonore qui unifie l'expérience visiteur sur l'ensemble du site.",
  },
  {
    icon: Store,
    title: "Chaînes de magasins",
    text: "Une radio cohérente sur tous vos points de vente, partout dans le pays.",
  },
  {
    icon: Sparkles,
    title: "Showrooms & marques",
    text: "Sublimer un univers produit avec une bande-son brandée et exclusive.",
  },
  {
    icon: Crown,
    title: "Marques institutionnelles",
    text: "Transformer un point de contact en média propriétaire, durable et mesurable.",
  },
];

const services = [
  {
    icon: ShieldCheck,
    title: "Équipement professionnel",
    text: "Infrastructure audio stable, diffusion 24h/24 sans interruption.",
  },
  {
    icon: Activity,
    title: "Son haute fidélité",
    text: "Un rendu net et puissant, adapté aux espaces commerciaux et corporate.",
  },
  {
    icon: ListMusic,
    title: "Musique ciblée",
    text: "Sélection sur mesure selon votre identité, votre clientèle et vos saisons.",
  },
];

const strategy = [
  {
    icon: QrCode,
    title: "QR codes en magasin",
    text: "Vos clients flashent et continuent d'écouter votre radio sur leur trajet retour, à la maison, au bureau.",
  },
  {
    icon: Megaphone,
    title: "Flyers & supports print",
    text: "Une communication imprimée pour transformer chaque sac, chaque ticket de caisse en porte d'entrée vers votre radio.",
  },
  {
    icon: Headphones,
    title: "Présence WallTunes",
    text: "Votre radio rejoint le mur WallTunes — un canal de découverte additionnel utilisé par les auditeurs en voiture (CarPlay) et chez eux.",
  },
];

const packs = [
  {
    name: "Essentiel",
    price: "6 000 €",
    suffix: "+ 50 €/mois hébergement",
    desc: "Pour les marques ambitieuses, showrooms et restaurants haut de gamme.",
    features: [
      "PWA radio à votre marque",
      "Playlist initiale sur mesure",
      "Hébergement & diffusion 1 an inclus",
      "Présence sur le mur WallTunes",
      "Compatible CarPlay & Android Auto",
    ],
    cta: "Demander un devis",
    highlight: false,
  },
  {
    name: "Premium",
    price: "8 500 €",
    suffix: "+ 80 €/mois hébergement",
    desc: "Pour les chaînes de magasins et enseignes nationales.",
    features: [
      "Tout l'Essentiel",
      "Flyers print professionnels (design inclus)",
      "QR codes designés pour vos points de vente",
      "4 jingles personnalisés",
      "6 mises à jour playlist / an",
      "Statistiques d'écoute mensuelles",
    ],
    cta: "Choisir Premium",
    highlight: true,
  },
  {
    name: "Enterprise",
    price: "dès 12 000 €",
    suffix: "sur devis personnalisé",
    desc: "Pour les centres commerciaux, groupes et marques institutionnelles.",
    features: [
      "Tout le Premium",
      "Multi-sites & multi-radios",
      "Jingles publicitaires illimités",
      "Support prioritaire & SLA",
      "Intégration corporate (SSO, branding)",
      "Statistiques avancées & exports",
    ],
    cta: "Demander un devis",
    highlight: false,
  },
];

export default function HomePage() {
  return (
    <div className="mw-app">
      <MwHeader />

      {/* ===== HERO ===== */}
      <section className="mw-hero">
        <div className="mw-hero-grain" aria-hidden="true" />
        <div className="mw-hero-inner">
          <div className="mw-eyebrow">
            <span className="mw-dot" /> Studio depuis Dover, England
          </div>
          <h1 className="mw-h1">
            Donnez une voix à votre marque.
            <br />
            <span className="mw-h1-accent">Partout, en continu.</span>
          </h1>
          <p className="mw-lead">
            MediaWebRadio crée des radios internet professionnelles pour les
            centres commerciaux, chaînes de magasins, showrooms et marques
            institutionnelles. Une bande-son exclusive qui suit vos clients
            au-delà de vos points de vente — grâce à nos QR codes et flyers
            stratégiques.
          </p>
          <div className="mw-hero-cta">
            <Link to="/contact" className="mw-btn mw-btn-primary" data-testid="hero-cta-quote">
              Demander un devis
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/offre-pwa" className="mw-btn mw-btn-ghost" data-testid="hero-cta-packs">
              Voir nos forfaits
            </Link>
          </div>
        </div>
      </section>

      {/* ===== POUR QUI ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Pour qui ?</span>
          <h2 className="mw-h2">Une solution conçue pour les grandes marques</h2>
          <p className="mw-section-lead">
            Notre approche s'adresse aux structures qui pensent leur ambiance
            sonore comme un investissement marketing à long terme — pas comme
            une simple playlist Spotify partagée.
          </p>
        </div>

        <div className="mw-cards-4">
          {targets.map(({ icon: Icon, title, text }) => (
            <article key={title} className="mw-card">
              <span className="mw-card-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="mw-card-title">{title}</h3>
              <p className="mw-card-text">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ===== STRATÉGIE QR + FLYERS ===== */}
      <section className="mw-section mw-section-strategy">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Notre méthode</span>
          <h2 className="mw-h2">Une radio qui vit au-delà du magasin</h2>
          <p className="mw-section-lead">
            Diffuser dans vos espaces ne suffit pas. Nous bâtissons l'écosystème
            qui prolonge l'écoute : à la maison, en voiture, au bureau. Votre
            radio devient un média propriétaire pérenne.
          </p>
        </div>

        <div className="mw-cards-3">
          {strategy.map(({ icon: Icon, title, text }) => (
            <article key={title} className="mw-card">
              <span className="mw-card-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="mw-card-title">{title}</h3>
              <p className="mw-card-text">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Nos services</span>
          <h2 className="mw-h2">Du savoir-faire humain, pas de l'IA</h2>
          <p className="mw-section-lead">
            Contrairement aux solutions automatisées, chaque radio est installée,
            configurée et accompagnée manuellement par un professionnel.
          </p>
        </div>

        <div className="mw-cards-3">
          {services.map(({ icon: Icon, title, text }) => (
            <article key={title} className="mw-card">
              <span className="mw-card-icon" aria-hidden="true">
                <Icon size={20} strokeWidth={1.8} />
              </span>
              <h3 className="mw-card-title">{title}</h3>
              <p className="mw-card-text">{text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ===== TARIFS (3 paliers) ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Nos forfaits</span>
          <h2 className="mw-h2">Création de PWA radio professionnelle</h2>
          <p className="mw-section-lead">
            Tarifs clairs, prestations détaillées. Chaque projet débute par un
            entretien stratégique gratuit pour valider votre brief.
          </p>
        </div>

        <div className="mw-packs">
          {packs.map((p) => (
            <article
              key={p.name}
              className={p.highlight ? "mw-pack mw-pack-highlight" : "mw-pack"}
              data-testid={`pack-${p.name.toLowerCase()}`}
            >
              {p.highlight && <div className="mw-pack-ribbon">Le plus choisi</div>}
              <div className="mw-pack-name">{p.name}</div>
              <div className="mw-pack-price">{p.price}</div>
              <div className="mw-pack-suffix">{p.suffix}</div>
              <p className="mw-pack-desc">{p.desc}</p>
              <ul className="mw-pack-list">
                {p.features.map((f) => (
                  <li key={f}>
                    <Check size={14} strokeWidth={2.4} className="mw-pack-check" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/contact"
                className={
                  p.highlight
                    ? "mw-btn mw-btn-primary mw-pack-cta"
                    : "mw-btn mw-btn-ghost mw-pack-cta"
                }
              >
                {p.cta} <ArrowUpRight size={14} />
              </Link>
            </article>
          ))}
        </div>

        <div className="mw-pack-note">
          Tous nos forfaits incluent un entretien stratégique préalable et une
          installation manuelle par un professionnel. Devis et facturation par
          MediaWebRadio Ltd, Dover (UK).
        </div>
      </section>

      {/* ===== OFFRE WALLTUNES 50€/an ===== */}
      <section className="mw-section">
        <div className="mw-section-head">
          <span className="mw-section-eyebrow">Une alternative pour les radios indépendantes</span>
          <h2 className="mw-h2">Diffusez votre flux sur WallTunes — 50 €/an</h2>
          <p className="mw-section-lead">
            Vous avez déjà votre propre webradio et voulez juste être présent sur
            notre mur WallTunes (compatible CarPlay & Android Auto) ?
            Découvrez notre offre Stream-only.
          </p>
        </div>

        <div className="mw-stream-card">
          <div className="mw-stream-card-inner">
            <div className="mw-stream-card-left">
              <span className="mw-pack-ribbon mw-stream-ribbon">
                <Radio size={12} /> Stream-only · 50 €/an
              </span>
              <h3 className="mw-stream-title">
                Votre webradio sur WallTunes, sans développer de PWA
              </h3>
              <p className="mw-stream-desc">
                Envoyez-nous votre lien de stream. On intègre votre logo, vos
                métadonnées Now Playing, les pochettes haute définition et la
                compatibilité CarPlay / Android Auto. Plus simple, plus rapide et
                bien moins cher qu'une PWA dédiée.
              </p>
              <ul className="mw-stream-list">
                <li><Check size={14} className="mw-pack-check" /> Logo + couleur de marque</li>
                <li><Check size={14} className="mw-pack-check" /> Métadonnées Now Playing temps réel</li>
                <li><Check size={14} className="mw-pack-check" /> Pochettes d'album HD automatiques</li>
                <li><Check size={14} className="mw-pack-check" /> CarPlay & Android Auto inclus</li>
              </ul>
            </div>
            <div className="mw-stream-card-right">
              <div className="mw-stream-price">50 €<span>/ an</span></div>
              <Link
                to="/offre-stream"
                className="mw-btn mw-btn-primary"
                data-testid="stream-offer-cta"
              >
                En savoir plus <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA FINAL ===== */}
      <section className="mw-section mw-section-final">
        <div className="mw-final">
          <h2 className="mw-h2">Prêt à créer la radio de votre marque ?</h2>
          <p className="mw-lead">
            Parlons de votre projet. Entretien stratégique gratuit, devis
            personnalisé sous 48 h, mise en place en 4 semaines.
          </p>
          <div className="mw-hero-cta">
            <Link to="/contact" className="mw-btn mw-btn-primary" data-testid="final-cta-contact">
              Démarrer mon projet
              <ArrowUpRight size={16} />
            </Link>
            <Link to="/wall" className="mw-btn mw-btn-ghost">
              Écouter WallTunes
              <Headphones size={16} />
            </Link>
          </div>
        </div>
      </section>

      <MwFooter />
    </div>
  );
}
